import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service | Drpr.ai";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-foreground" data-testid="terms-page">
      <header className="border-b border-foreground/10 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <Link href="/" data-testid="link-home">
            <span className="text-xl font-bold tracking-tight cursor-pointer">Drpr.ai</span>
          </Link>
          <Link href="/" data-testid="link-back-home">
            <span className="flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground transition-colors cursor-pointer">
              <ArrowLeft size={16} />
              Back to Home
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-2 mb-12">
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="text-terms-title">Terms of Service</h1>
          <p className="text-foreground/50 text-sm">Last Updated: February 16, 2026</p>
        </div>

        <div className="space-y-10 text-foreground/80 leading-relaxed text-[15px]">
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">1. Acceptance of Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you
              ("User," "you," or "your") and Lejjors Corp. ("Company," "we," "us," or "our"),
              governing your access to and use of the Drpr.ai platform and related services
              (collectively, the "Service") available at drpr.ai.
            </p>
            <p>
              By creating an account, accessing, or using the Service, you acknowledge that you have
              read, understood, and agree to be bound by these Terms and our Privacy Policy. If you
              do not agree to these Terms, you must not access or use the Service.
            </p>
            <p>
              You must be at least 18 years of age to use the Service. By using the Service, you
              represent and warrant that you are at least 18 years old and have the legal capacity
              to enter into these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">2. Description of Service</h2>
            <p>
              Drpr.ai is an autonomous AI-powered marketing SaaS platform that provides the following
              services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>AI-generated marketing strategy development and recommendations</li>
              <li>Automated content creation and management</li>
              <li>Social media management and publishing across Meta (Facebook/Instagram), LinkedIn, and X</li>
              <li>Advertising campaign creation, management, and optimization</li>
              <li>Lead generation, management, and sales funnel tools</li>
              <li>Landing page and funnel builder</li>
              <li>Marketing analytics and performance tracking</li>
              <li>AI-powered marketing agent assistance</li>
            </ul>
            <p>
              The Service utilizes artificial intelligence, including Google Gemini AI, to automate
              and enhance marketing activities. AI-generated outputs are provided as recommendations
              and tools; you are responsible for reviewing and approving all content before publication.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">3. User Accounts</h2>

            <h3 className="text-lg font-medium text-foreground/90">3.1 Account Registration</h3>
            <p>
              To access the Service, you must create an account by providing accurate, current, and
              complete information. You are responsible for maintaining the accuracy of your account
              information and updating it as necessary.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">3.2 Account Security</h3>
            <p>
              You are responsible for safeguarding your account credentials and for all activities
              that occur under your account. You must immediately notify us of any unauthorized use
              of your account or any other breach of security. We will not be liable for any loss or
              damage arising from your failure to maintain account security.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">3.3 Workspace and Team Access</h3>
            <p>
              You may create workspaces and invite team members to collaborate within the Service.
              You are responsible for the actions of all users you invite to your workspace and for
              ensuring they comply with these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">4. Acceptable Use</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Upload or distribute malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Create or distribute spam, unsolicited messages, or deceptive content</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Scrape, mine, or extract data from the Service without authorization</li>
              <li>Resell, sublicense, or redistribute the Service without our written consent</li>
              <li>Use the Service in a manner that violates the terms of connected third-party platforms (Meta, LinkedIn, X)</li>
              <li>Generate content that is illegal, harmful, defamatory, obscene, or that infringes on the rights of others</li>
              <li>Misrepresent AI-generated content as human-created where such disclosure is required by law</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">5. Intellectual Property</h2>

            <h3 className="text-lg font-medium text-foreground/90">5.1 Our Intellectual Property</h3>
            <p>
              The Service, including its original content, features, functionality, design, and
              underlying technology, is and remains the exclusive property of Lejjors Corp. and its
              licensors. The Service is protected by copyright, trademark, and other intellectual
              property laws. Our trademarks, service marks, and logos may not be used without our
              prior written consent.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">5.2 Your Content</h3>
            <p>
              You retain ownership of the content you create, upload, or input into the Service
              ("Your Content"). By using the Service, you grant us a limited, non-exclusive,
              worldwide license to use, process, store, and display Your Content solely for the
              purpose of providing and improving the Service.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">5.3 AI-Generated Content</h3>
            <p>
              Content generated by the AI features of the Service based on your inputs is considered
              Your Content. You are responsible for reviewing, editing, and ensuring the accuracy
              and appropriateness of all AI-generated content before use or publication. We make no
              warranties regarding the accuracy, completeness, or suitability of AI-generated content.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">5.4 Feedback</h3>
            <p>
              Any feedback, suggestions, or ideas you provide regarding the Service may be used by
              us without obligation to you. You assign to us all rights in such feedback.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">6. Payment Terms</h2>

            <h3 className="text-lg font-medium text-foreground/90">6.1 Subscription Plans</h3>
            <p>
              The Service may offer various subscription plans with different features and pricing.
              Details of available plans, pricing, and included features are displayed on the
              Service at the time of purchase.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">6.2 Payment Processing</h3>
            <p>
              All payments are processed securely through Stripe. By subscribing to a paid plan,
              you authorize us to charge the payment method on file through Stripe for the
              applicable subscription fees. You agree to provide current, complete, and accurate
              billing information.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">6.3 Billing Cycle and Renewals</h3>
            <p>
              Subscription fees are billed in advance on a recurring basis (monthly or annually,
              depending on your selected plan). Your subscription will automatically renew at the
              end of each billing cycle unless you cancel before the renewal date.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">6.4 Refunds</h3>
            <p>
              Subscription fees are generally non-refundable. However, if you are dissatisfied with
              the Service, you may contact us at admin@lejjors.com to discuss your situation. Refund
              requests will be evaluated on a case-by-case basis at our sole discretion.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">6.5 Price Changes</h3>
            <p>
              We reserve the right to modify our pricing at any time. Any price changes will take
              effect at the start of the next billing cycle following notice to you. Continued use
              of the Service after a price change constitutes your acceptance of the new pricing.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">7. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party platforms and services. Your use of these
              integrations is subject to the terms and policies of those third parties:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Meta (Facebook/Instagram):</strong> Subject to Meta's Terms of Service and Platform Policies</li>
              <li><strong className="text-foreground">LinkedIn:</strong> Subject to LinkedIn's User Agreement and Professional Community Policies</li>
              <li><strong className="text-foreground">X (formerly Twitter):</strong> Subject to X's Terms of Service and Developer Agreement</li>
              <li><strong className="text-foreground">Google Gemini AI:</strong> Subject to Google's Terms of Service and AI usage policies</li>
              <li><strong className="text-foreground">Stripe:</strong> Subject to Stripe's Services Agreement</li>
            </ul>
            <p>
              We are not responsible for the actions, content, or policies of third-party services.
              Your use of third-party integrations is at your own risk.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">8. Service Availability and Modifications</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee uninterrupted
              access. We may modify, suspend, or discontinue any part of the Service at any time, with
              or without notice. We reserve the right to perform maintenance that may temporarily affect
              service availability.
            </p>
            <p>
              We may update, modify, or enhance the Service's features and functionality at our
              discretion. Material changes that adversely affect your use of the Service will be
              communicated in advance when reasonably possible.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LEJJORS CORP.,
              ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT
              LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your access to or use of (or inability to access or use) the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service, including AI-generated content</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              <li>Actions taken on connected social media platforms based on Service recommendations</li>
              <li>Performance or results of advertising campaigns managed through the Service</li>
              <li>Loss of leads, customers, or business opportunities</li>
            </ul>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL
              NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF
              ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND COURSE OF
              DEALING.
            </p>
            <p>
              WE DO NOT WARRANT THAT: (A) THE SERVICE WILL FUNCTION UNINTERRUPTED, SECURE, OR
              AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; (B) ANY ERRORS OR DEFECTS WILL BE
              CORRECTED; (C) THE SERVICE IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; (D) THE
              RESULTS OF USING THE SERVICE, INCLUDING AI-GENERATED CONTENT AND MARKETING
              RECOMMENDATIONS, WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">11. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Lejjors Corp. and its officers,
              directors, employees, and agents from and against any claims, damages, obligations,
              losses, liabilities, costs, or debt arising from: (a) your use of the Service; (b) your
              violation of these Terms; (c) your violation of any third-party right, including
              intellectual property or privacy rights; (d) content you create, publish, or distribute
              through the Service; (e) your use of connected third-party platforms through the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">12. Termination</h2>

            <h3 className="text-lg font-medium text-foreground/90">12.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by contacting us at admin@lejjors.com or
              through your account settings. Upon termination, your right to use the Service will
              immediately cease.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">12.2 Termination by Us</h3>
            <p>
              We may terminate or suspend your account immediately, without prior notice or liability,
              for any reason, including if you breach these Terms, engage in fraudulent activity, or
              fail to pay applicable fees. We may also terminate accounts that are inactive for an
              extended period.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">12.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service ceases immediately. We may delete your
              account data within 30 days of termination, subject to any legal retention requirements.
              Sections of these Terms that by their nature should survive termination shall survive,
              including intellectual property, limitation of liability, indemnification, and governing law.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">13. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State
              of Delaware, United States, without regard to its conflict of law provisions.
            </p>
            <p>
              Any disputes arising out of or relating to these Terms or the Service shall first be
              attempted to be resolved through good-faith negotiation. If the dispute cannot be
              resolved through negotiation within 30 days, it shall be submitted to binding
              arbitration in the State of Delaware in accordance with the rules of the American
              Arbitration Association.
            </p>
            <p>
              You agree that any dispute resolution proceedings will be conducted on an individual
              basis and not in a class, consolidated, or representative action. You waive any right
              to participate in class actions against Lejjors Corp.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">14. Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time at our sole discretion.
              If a revision is material, we will provide at least 30 days' notice prior to any new
              terms taking effect by posting the updated Terms on the Service and updating the "Last
              Updated" date. We may also notify you by email.
            </p>
            <p>
              Your continued use of the Service after the revised Terms take effect constitutes your
              acceptance of the new Terms. If you do not agree to the new Terms, you must stop using
              the Service and close your account.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">15. Miscellaneous</h2>

            <h3 className="text-lg font-medium text-foreground/90">15.1 Entire Agreement</h3>
            <p>
              These Terms, together with the Privacy Policy and any other legal notices published on
              the Service, constitute the entire agreement between you and Lejjors Corp. regarding
              the Service.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">15.2 Severability</h3>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, the remaining
              provisions shall continue in full force and effect.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">15.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a
              waiver of those rights.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">15.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms without our prior written consent. We may
              assign our rights and obligations under these Terms without restriction.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">15.5 Force Majeure</h3>
            <p>
              We shall not be liable for any failure to perform our obligations where such failure
              results from circumstances beyond our reasonable control, including but not limited to
              natural disasters, acts of government, internet outages, or third-party service failures.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">16. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-foreground/5 border border-foreground/10 rounded-md p-6 space-y-2">
              <p><strong className="text-foreground">Lejjors Corp.</strong></p>
              <p>Product: Drpr.ai</p>
              <p>Email: <a href="mailto:admin@lejjors.com" className="text-blue-400 hover:underline" data-testid="link-contact-email">admin@lejjors.com</a></p>
              <p>Website: <a href="https://drpr.ai" className="text-blue-400 hover:underline" data-testid="link-website">drpr.ai</a></p>
            </div>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-foreground/10 flex items-center justify-between gap-4 flex-wrap text-sm text-foreground/40">
          <p>&copy; {new Date().getFullYear()} Lejjors Corp. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/terms" data-testid="link-terms">
              <span className="hover:text-foreground/70 transition-colors cursor-pointer">Terms of Service</span>
            </Link>
            <Link href="/privacy" data-testid="link-privacy">
              <span className="hover:text-foreground/70 transition-colors cursor-pointer">Privacy Policy</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}